import { useRef, useState, type FormEvent } from 'react'
import { useToastStore } from '@/lib/toastStore'
import { useAddActivity } from '@/features/activity/useActivities'
import RichTextEditor, { type RichTextEditorHandle } from '@/components/RichTextEditor'
import Card from '@/components/layout/Card'

/**
 * The deal's "Notes" card — the first thing an agent reaches for after a call, and the top of the
 * Overview's notes → tasks → send-message order. Deliberately note-only: the activity log keeps the
 * channel picker out of the way so a note can never be mistaken for a message actually sent to the
 * client. Saves straight to the timeline (no deal-level Save needed).
 */
export default function DealNotes({ dealId }: { dealId: string }) {
  const toast = useToastStore((t) => t.push)
  const add = useAddActivity(dealId)
  const [body, setBody] = useState('')
  const editorRef = useRef<RichTextEditorHandle>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    if (!body.trim()) return
    add.mutate({ activityType: 'note', body }, { onSuccess: () => { setBody(''); toast('Note saved — it’s at the top of Activity', 'success') } })
  }

  return (
    <Card title="Notes">
      <form onSubmit={submit}>
        <RichTextEditor
          ref={editorRef}
          value={body}
          onChange={setBody}
          placeholder="What did you discuss? Notes save to the activity timeline."
        />
        <div className="flex items-center justify-end mt-2">
          <button
            type="submit"
            disabled={add.isPending || !body.trim()}
            className="px-3 py-2 bg-brand-primary text-white rounded-lg text-xs font-medium hover:bg-brand-dark disabled:opacity-60"
          >
            {add.isPending ? 'Saving…' : 'Save note'}
          </button>
        </div>
      </form>
    </Card>
  )
}
